<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import { useRouter } from 'vue-router'
import { useI18n } from 'vue-i18n'
import { useAuthStore } from '../stores/auth'
import { useToastStore } from '../stores/toast'
import playerImportService from '../services/playerImport.service'
import {
  IMPORT_FIELDS,
  loadImportState,
  saveImportState,
  buildReviewRows,
  cleanValue,
  type PlayerImportState,
} from '../composables/usePlayerImport'

const router = useRouter()
const authStore = useAuthStore()
const toastStore = useToastStore()
const { t } = useI18n()

const clubUuid = computed(() => authStore.initData?.club?.uuid || '')

const state = ref<PlayerImportState | null>(null)
const processing = ref(false)

const rows = computed(() => state.value?.rows || [])
const columnCount = computed(() => rows.value.reduce((max, row) => Math.max(max, row.length), 0))

const columnLetter = (index: number) => {
  let letter = ''
  let n = index + 1
  while (n > 0) {
    const rem = (n - 1) % 26
    letter = String.fromCharCode(65 + rem) + letter
    n = Math.floor((n - 1) / 26)
  }
  return letter
}

const startRowIndex = computed(() => (state.value ? state.value.startRow - 1 : 0))
const headerRow = computed(() => rows.value[startRowIndex.value - 1] || [])
const previewRow = computed(() => rows.value[startRowIndex.value] || [])
const dataRowsCount = computed(() => Math.max(rows.value.length - startRowIndex.value, 0))

// Only non-empty columns are offered in the selectors
const columnOptions = computed(() => {
  const options: Array<{ index: number, label: string }> = []
  for (let i = 0; i < columnCount.value; i++) {
    const header = headerRow.value[i] || ''
    const hasData = rows.value.slice(startRowIndex.value).some(r => (r[i] || '').trim() !== '')
    if (!header && !hasData) continue
    const sample = cleanValue(previewRow.value[i])
    options.push({
      index: i,
      label: `${columnLetter(i)}${header ? `: ${header}` : ''}${sample ? ` (${sample})` : ''}`,
    })
  }
  return options
})

const isMappingValid = computed(() => {
  const mapping = state.value?.mapping
  return !!mapping && IMPORT_FIELDS.filter(f => f.required).every(f => mapping[f.key] !== null && mapping[f.key] !== undefined)
})

const onStartRowChange = (event: Event) => {
  if (!state.value) return
  const value = Number((event.target as HTMLInputElement).value)
  state.value.startRow = Math.min(Math.max(1, Math.floor(value) || 1), Math.max(rows.value.length, 1))
  saveImportState(state.value)
}

const onMappingChange = () => {
  if (state.value) saveImportState(state.value)
}

const goToReview = async () => {
  if (!state.value?.mapping || !isMappingValid.value) return

  processing.value = true
  try {
    const reviewRows = buildReviewRows(state.value.rows, state.value.startRow, state.value.mapping)
    if (!reviewRows.length) {
      toastStore.error(t('playerImport.noRowsFound'))
      return
    }

    try {
      const duplicates = await playerImportService.checkDuplicates(
        clubUuid.value,
        reviewRows.map(r => ({ first_name: r.first_name, last_name: r.last_name, birth_date: r.birth_date || null }))
      )
      duplicates.forEach(index => {
        if (reviewRows[index]) reviewRows[index].duplicate = true
      })
    } catch (err) {
      console.error('Check duplicates error:', err)
    }

    state.value.reviewRows = reviewRows
    saveImportState(state.value)
    router.push('/players/import/review')
  } finally {
    processing.value = false
  }
}

onMounted(() => {
  state.value = loadImportState(clubUuid.value)
  if (!state.value) {
    router.replace('/dashboard')
  }
})
</script>

<template>
  <main class="bg-gray-50 min-h-screen">
    <div v-if="state" class="w-full px-4 py-4 md:py-4 py-2">
      <div class="mb-4 flex justify-between items-center gap-2">
        <div>
          <h1 class="text-2xl font-bold text-gray-900">{{ t('playerImport.mappingTitle') }}</h1>
          <p class="text-sm text-gray-600">Sport BM · {{ state.fileName }}</p>
        </div>
        <router-link to="/dashboard" class="text-sm text-blue-600 hover:text-blue-800 hover:underline">
          {{ t('common.back') }}
        </router-link>
      </div>

      <!-- 1. Start row -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-4">1. {{ t('playerImport.startRowQuestion') }}</h2>
        <div class="flex items-center gap-3 mb-4">
          <input
            type="number"
            min="1"
            :max="rows.length"
            :value="state.startRow"
            class="w-28 px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
            @change="onStartRowChange"
          >
          <span class="text-sm text-gray-600">{{ t('playerImport.rowsToImport', { count: dataRowsCount }) }}</span>
        </div>

        <p class="text-sm font-medium text-gray-700 mb-2">{{ t('playerImport.previewRow', { row: state.startRow }) }}</p>
        <div class="overflow-x-auto border-2 border-gray-200 rounded">
          <table class="min-w-full text-sm">
            <thead class="bg-gray-50">
              <tr>
                <th
                  v-for="option in columnOptions"
                  :key="option.index"
                  class="px-3 py-2 text-left text-xs font-semibold text-gray-600 whitespace-nowrap border-b border-gray-200"
                >
                  {{ columnLetter(option.index) }}<span v-if="headerRow[option.index]">: {{ headerRow[option.index] }}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td
                  v-for="option in columnOptions"
                  :key="option.index"
                  class="px-3 py-2 text-gray-900 whitespace-nowrap"
                >
                  {{ previewRow[option.index] || '' }}
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>

      <!-- 2. Column mapping -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 mb-4">
        <h2 class="text-lg font-semibold text-gray-900 mb-1">2. {{ t('playerImport.mappingQuestion') }}</h2>
        <p class="text-sm text-gray-600 mb-4">{{ t('playerImport.mappingHint') }}</p>

        <div class="grid grid-cols-1 md:grid-cols-2 gap-x-8 gap-y-3">
          <div
            v-for="field in IMPORT_FIELDS"
            :key="field.key"
            class="grid grid-cols-1 md:grid-cols-[180px_1fr] gap-2 md:items-center"
          >
            <div class="text-sm font-medium text-gray-900">
              {{ t(`playerImport.fields.${field.key}`) }}
              <span v-if="field.required" class="text-red-600">{{ t('common.required') }}</span>
            </div>
            <select
              v-model="state.mapping![field.key]"
              class="w-full px-3 py-2 text-sm border-2 border-gray-300 rounded focus:outline-none focus:border-blue-600"
              @change="onMappingChange"
            >
              <option :value="null">{{ t('playerImport.doNotImport') }}</option>
              <option v-for="option in columnOptions" :key="option.index" :value="option.index">
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>

        <p class="mt-4 text-xs text-gray-500">{{ t('playerImport.mappingInfo') }}</p>
      </div>

      <!-- Next step -->
      <div class="bg-white border-2 border-gray-300 rounded shadow-sm p-6 flex flex-col md:flex-row md:items-center md:justify-end gap-3">
        <p class="text-sm text-gray-700 md:text-right">{{ t('playerImport.goToReviewText') }}</p>
        <button
          type="button"
          :disabled="!isMappingValid || processing"
          class="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded hover:bg-blue-700 border-2 border-blue-600 disabled:opacity-50 disabled:cursor-not-allowed self-start md:self-auto"
          @click="goToReview"
        >
          {{ processing ? t('playerImport.processing') : t('playerImport.goToReview') }}
        </button>
      </div>
    </div>
  </main>
</template>
